import { useEffect, useMemo, useRef, useState } from "react";
import { geoOrthographic, geoPath, geoInterpolate } from "d3-geo";
import { ZONES, ZONE6 } from "@/components/z6/zones";

const SIZE = 520;
const overlaps = (a, b) => a[0] < b[2] && a[2] > b[0] && a[1] < b[3] && a[3] > b[1];

// Greedy label placement (same idea as the WebGL globe): try offsets until a label clears markers and other labels.
function placeLabels(all, proj, selectedKey) {
  const scale = Math.min(window.innerWidth * 0.78, 560) / SIZE;
  const vis = all.map((z) => ({ z, p: proj([z.lng, z.lat]) })).filter(({ z, p }) => p && geoPath(proj)({ type: "Point", coordinates: [z.lng, z.lat] }))
    .map(({ z, p }) => ({ z, x: (p[0] - SIZE / 2) * scale, y: (p[1] - SIZE / 2) * scale, w: z.label.length * 7 + 44, h: 30 }));
  const rank = (it) => (it.z.key === selectedKey ? 2 : 0) + (it.z.key === "zone-6" ? 1 : 0);
  vis.sort((a, b) => rank(b) - rank(a));
  const placed = vis.map((it) => [it.x - 6, it.y - 6, it.x + 6, it.y + 6]);
  return vis.map((it) => {
    const c = [[0, -(it.h / 2 + 12)], [0, it.h / 2 + 12], [it.w / 2 + 14, 0], [-(it.w / 2 + 14), 0], [it.w / 2 + 8, -(it.h / 2 + 14)], [-(it.w / 2 + 8), -(it.h / 2 + 14)], [0, -(it.h + 30)], [0, it.h + 30]];
    let off = c[0];
    for (const o of c) {
      const r = [it.x + o[0] - it.w / 2, it.y + o[1] - it.h / 2, it.x + o[0] + it.w / 2, it.y + o[1] + it.h / 2];
      if (!placed.some((q, k) => vis[k] !== it && overlaps(q, r))) { off = o; placed.push(r); break; }
    }
    return { ...it, lx: it.x + off[0], ly: it.y + off[1] };
  });
}

export const GlobeFallback = ({ onSelect, selectedKey }) => {
  const [dots, setDots] = useState([]);
  const [rot, setRot] = useState([-40, -18]);
  const drag = useRef(null);
  useEffect(() => { fetch("/globe-dots.json").then((r) => r.json()).then(setDots); }, []);
  const size = SIZE;
  const proj = useMemo(() => geoOrthographic().scale(SIZE / 2.15).translate([SIZE / 2, SIZE / 2]).rotate(rot).clipAngle(90), [rot]);
  const path = geoPath(proj);
  const pts = useMemo(() => {
    const out = [];
    for (let i = 0; i < dots.length; i += 2) { const p = proj([dots[i + 1], dots[i]]); if (p) out.push(p); }
    return out;
  }, [dots, proj]);
  const all = [...ZONES, ZONE6];
  return (
    <div data-testid="globe-fallback" className="absolute inset-0 flex items-center justify-center touch-none"
      onPointerDown={(e) => { drag.current = { x: e.clientX, y: e.clientY, r: rot }; }}
      onPointerMove={(e) => { if (drag.current) setRot([drag.current.r[0] + (e.clientX - drag.current.x) * 0.35, Math.max(-60, Math.min(60, drag.current.r[1] - (e.clientY - drag.current.y) * 0.35))]); }}
      onPointerUp={() => { drag.current = null; }} onPointerLeave={() => { drag.current = null; }}>
      <svg viewBox={`0 0 ${size} ${size}`} className="h-[min(78vw,560px)] w-[min(78vw,560px)]">
        <defs><radialGradient id="gf" cx="40%" cy="35%"><stop offset="0%" stopColor="#BFE8EC" stopOpacity="0.55" /><stop offset="100%" stopColor="#F2FBFB" stopOpacity="0.2" /></radialGradient></defs>
        <circle cx={size / 2} cy={size / 2} r={size / 2.15} fill="url(#gf)" stroke="#E8508A" strokeOpacity="0.7" strokeWidth="1.5" />
        {pts.map(([x, y], i) => <circle key={i} cx={x} cy={y} r="1.3" fill="#2BB3C0" opacity="0.85" />)}
        {ZONES.map((z) => {
          const interp = geoInterpolate([z.lng, z.lat], [ZONE6.lng, ZONE6.lat]);
          const d = path({ type: "LineString", coordinates: Array.from({ length: 30 }, (_, i) => interp(i / 29)) });
          return d ? <path key={z.key} d={d} fill="none" stroke="#E8508A" strokeOpacity="0.75" strokeWidth="1.2" className="arc-dash" /> : null;
        })}
      </svg>
      {placeLabels(all, proj, selectedKey).map(({ z, x, y, lx, ly }) => (
        <button key={z.key} data-testid={`zone-marker-${z.key}`} onClick={() => onSelect(z)}
          style={{ transform: `translate(${lx}px, ${ly}px)` }}
          className={`font-ui absolute inline-flex min-h-[48px] items-center rounded-full px-4 py-3 text-[15px] ${selectedKey === z.key ? "bg-transparent ring-1 ring-[#E8508A] hover:bg-[#E8508A]/10 text-[#B8326C]" : "bg-white/90 text-[#0B2E44]"}`}>
          <span className="mr-1.5 inline-block h-2 w-2 rounded-full" style={{ background: z.color, boxShadow: `0 0 10px ${z.color}` }} />{z.label}
        </button>
      ))}
    </div>
  );
};
